import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { adminBklsOverview } from "@/lib/bkls-wizard.functions";
import { WORKFLOW_LABEL, WORKFLOW_ORDER } from "@/lib/bkls-rights";

export const Route = createFileRoute("/_authenticated/admin/bkls/")({
  head: () => ({ meta: [{ title: "BKLS Overview · Buddha KA" }, { name: "robots", content: "noindex" }] }),
  component: BklsOverview,
});

const card = "rounded-2xl border hairline bg-charcoal p-6";

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-xl border hairline bg-obsidian p-4">
      <p className="text-[10px] uppercase tracking-[0.2em] text-gold/80">{label}</p>
      <p className="mt-2 font-serif text-2xl text-ivory">{value}</p>
    </div>
  );
}

function BklsOverview() {
  const load = useServerFn(adminBklsOverview);
  const overview = useQuery({
    queryKey: ["bkls", "overview"],
    queryFn: () => load({ data: {} } as never),
  });

  const data = overview.data;
  const totals = data?.totals;

  return (
    <div className="space-y-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Licensing</p>
          <h1 className="mt-2 font-serif text-3xl">BKLS overview.</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to="/admin/bkls/phase10"
            className="rounded-full bg-gold-gradient px-4 py-2 text-xs font-semibold text-obsidian hover:opacity-90"
          >
            Phase 10 · Live activation
          </Link>
          <Link
            to="/admin/bkls/wizard"
            search={{ id: undefined }}
            className="rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian"
          >
            New beat
          </Link>
          <Link to="/admin/bkls/audit" className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold">
            Audit centre
          </Link>
          <Link to="/admin/bkls/readiness" className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold">
            Launch readiness
          </Link>
          <Link to="/beats/admin" className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold">
            Tiers, templates &amp; settings
          </Link>
        </div>
      </header>

      {overview.isLoading && <p className="text-sm text-muted-foreground">Loading real catalog data…</p>}

      {totals && (
        <>
          <section className={card} aria-labelledby="workflow-counts">
            <h2 id="workflow-counts" className="font-serif text-xl text-gold-luminous">
              Workflow
            </h2>
            {totals.beats === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">
                The catalog is empty. Nothing is published, and no placeholder data is shown. Start with{" "}
                <Link to="/admin/bkls/wizard" search={{ id: undefined }} className="text-gold hover:text-gold-luminous">
                  a new beat
                </Link>
                .
              </p>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {WORKFLOW_ORDER.map((state) => (
                  <Stat key={state} label={WORKFLOW_LABEL[state]} value={totals.byWorkflow[state] ?? 0} />
                ))}
              </div>
            )}
          </section>

          <section className={card} aria-labelledby="completeness">
            <h2 id="completeness" className="font-serif text-xl text-gold-luminous">
              Completeness
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Total beats" value={totals.beats} />
              <Stat label="Rights verified" value={totals.rightsComplete} />
              <Stat label="With files" value={totals.withFiles} />
              <Stat label="With a live tier" value={totals.withEnabledTier} />
              <Stat label="Duplicate checksums" value={totals.duplicateChecksums} />
              <Stat label="Open requests" value={totals.openRequests} />
              <Stat label="Issued licences" value={totals.issuedLicenses} />
              <Stat label="Collections" value={data.collections.length} />
            </div>
          </section>

          <section className={card} aria-labelledby="attention">
            <h2 id="attention" className="font-serif text-xl text-gold-luminous">
              Needs attention
            </h2>
            {data.needsAttention.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">
                {totals.beats === 0
                  ? "Nothing to review — the catalog has no beats yet."
                  : "Every unpublished beat has rights, a preview and at least one configured tier."}
              </p>
            ) : (
              <ul className="mt-4 space-y-2">
                {data.needsAttention.map((b: any) => (
                  <li
                    key={b.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border hairline bg-obsidian p-4"
                  >
                    <div>
                      <p className="text-sm text-ivory">{b.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {WORKFLOW_LABEL[b.workflow_status as keyof typeof WORKFLOW_LABEL]} ·{" "}
                        {[
                          !b.rightsComplete ? "rights incomplete" : null,
                          !b.hasPreview ? "no preview" : null,
                          b.enabledTiers === 0 ? "no licence tier" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <Link
                      to="/admin/bkls/wizard"
                      search={{ id: b.id }}
                      className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold hover:text-gold"
                    >
                      Resume
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={card} aria-labelledby="analytics">
            <h2 id="analytics" className="font-serif text-xl text-gold-luminous">
              Analytics
            </h2>
            {!data.analytics ? (
              <p className="mt-4 text-sm text-muted-foreground">
                No interaction events recorded yet. Metrics appear here once the public catalog receives traffic.
              </p>
            ) : (
              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Views" value={data.analytics.views} />
                <Stat label="Unique visitors" value={data.analytics.uniqueVisitors} />
                <Stat label="Preview plays" value={data.analytics.previewPlays} />
                <Stat label="Licence clicks" value={data.analytics.licenseClicks} />
              </div>
            )}
          </section>

          <section className={card} aria-labelledby="recent">
            <h2 id="recent" className="font-serif text-xl text-gold-luminous">
              Recent activity
            </h2>
            {data.recentActivity.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">No BKLS activity recorded yet.</p>
            ) : (
              <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                {data.recentActivity.map((a: any) => (
                  <li key={a.id}>
                    <span className="text-ivory">{a.action}</span> · {a.entity} ·{" "}
                    {new Date(a.created_at).toLocaleString()}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={card} aria-labelledby="catalog">
            <h2 id="catalog" className="font-serif text-xl text-gold-luminous">
              Catalog
            </h2>
            {data.beats.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">No beats exist yet.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="text-[10px] uppercase tracking-[0.2em] text-gold/80">
                    <tr>
                      <th scope="col" className="py-2 pr-4">Title</th>
                      <th scope="col" className="py-2 pr-4">Catalog no.</th>
                      <th scope="col" className="py-2 pr-4">State</th>
                      <th scope="col" className="py-2 pr-4">Tiers</th>
                      <th scope="col" className="py-2">Open</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.beats.map((b: any) => (
                      <tr key={b.id} className="border-t hairline">
                        <td className="py-2 pr-4 text-ivory">{b.title}</td>
                        <td className="py-2 pr-4 text-muted-foreground">{b.catalog_number ?? "—"}</td>
                        <td className="py-2 pr-4 text-muted-foreground">
                          {WORKFLOW_LABEL[b.workflow_status as keyof typeof WORKFLOW_LABEL]}
                        </td>
                        <td className="py-2 pr-4 text-muted-foreground">{b.enabledTiers}</td>
                        <td className="py-2">
                          <Link
                            to="/admin/bkls/wizard"
                            search={{ id: b.id }}
                            className="text-gold hover:text-gold-luminous"
                          >
                            Edit
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
